import { REPORTING_DOCUMENT_TYPES } from '@common/constants/client'
import { ROUTES } from '@common/constants/routes'
import { atlasUrls } from '@common/helpers/atlasUrls'
import { ClientInfoStatus } from '@common/interfaces/client'
import { CommentaryKind } from '@common/interfaces/commentary'
import { DueDiligenceDocumentRequestStep } from '@common/interfaces/dueDiligence'
import { NotesFilterOptions } from '@common/interfaces/notes'
import { NotificationType } from '@common/interfaces/notification'
import { OPSReportingStatus } from '@common/interfaces/prospects'

/** The columns `pickLatestActiveOpsReportingId` ranks a client's deals by. */
interface IRankableOpsReporting {
  id: string
  status?: OPSReportingStatus
  createdAt?: string | Date
}

/**
 * A commentary box as far as routing cares: which surface it belongs to
 * (`section.kind`) and which analysis owns it. Amendment and OPS write-up
 * commentaries carry no owner column on the note itself, so the analysis is the
 * only thing that names their page.
 */
interface INotificationRoutingCommentary {
  section?: { kind?: string } | null
  generalAnalysis?: {
    id: string
    /** The deal an OPS write-up belongs to; null on amendment analyses. */
    opsReportingId?: string | null
    clientInfo?: { id: string } | null
  } | null
}

export interface INotificationRoutingNote {
  id: string
  parentId?: string | null
  parent?: {
    flagId?: string | null
    /** Set when the note hangs off a commentary box (`notes.commentary_id`). */
    commentary?: INotificationRoutingCommentary | null
  } | null
  flagId?: string | null
  /**
   * The thread's root note, attached by the notification paths (see
   * `attachThreadRootCommentary`) for a reply nested more than one level deep.
   * Not a column — `notes.commentary_id` is never inherited by a reply, so
   * beyond depth 1 the root is the only note that still names the box.
   */
  threadRoot?: { id: string; commentary?: INotificationRoutingCommentary | null } | null
  commentary?: INotificationRoutingCommentary | null
  borrowingBase?: { id: string } | null
  opsReporting?: { id: string } | null
  ongoingReporting?: { id: string; type: string } | null
  /** Set on every note in a market news headline's reply thread. */
  newsfeedHeadlineId?: string | null
  clientInfo?: { id: string } | null
  noteMentions?: Array<{ entity?: unknown; entityId?: string | null }> | null
}

export interface INotificationRoutingData {
  type: NotificationType
  borrowingBaseId?: string | null
  clientInfo?: { id: string; opsReportings?: IRankableOpsReporting[] } | null
  ddDocumentRequestId?: string | null
  ddDocumentRequest?: { type?: { id?: string | null } | null } | null
  newsfeedHeadline?: { id?: string; link?: string } | null
  checkData?: { id?: string } | null
  note?: INotificationRoutingNote | null
  data?: Record<string, unknown>
}

export interface INotificationRoutingContext {
  isClientUser: boolean
  isBDO: boolean
  isUW: boolean
}

class MissingRouteParamError extends Error {}

function resolveRoute(template: string, params: Record<string, string>): string {
  return template.replace(/:([a-zA-Z][a-zA-Z0-9]*)/g, (_, key) => {
    if (params[key] == null) {
      throw new MissingRouteParamError(`Missing route param: ${key} in template: ${template}`)
    }
    return params[key]
  })
}

/**
 * A malformed notification — an owner relation loaded without its id — gets no
 * link rather than crashing the caller. Any other error is still a bug.
 */
function nullOnMissingRouteParam(resolve: () => string | null): string | null {
  try {
    return resolve()
  } catch (error) {
    if (error instanceof MissingRouteParamError) {
      return null
    }
    throw error
  }
}

function encodeQueryString(params: Record<string, string>): string {
  return Object.entries(params)
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
}

function buildIndexArrayQs(obj: Record<string, string[]>): string {
  const parts: string[] = []
  for (const [key, values] of Object.entries(obj)) {
    values.forEach((v, i) => {
      parts.push(`${encodeURIComponent(key)}[${i}]=${encodeURIComponent(v)}`)
    })
  }
  return parts.join('&')
}

function buildNoteSearch(noteId: string, extraFilters?: Record<string, string[]>): string {
  const inner = buildIndexArrayQs({ id: [noteId], ...extraFilters })
  return encodeQueryString({
    noteId,
    notesContainerFilters: inner,
  })
}

function withQuery(path: string, query: string): string {
  return query ? `${path}?${query}` : path
}

/**
 * The Messages deep link, shared by every surface that can open a conversation.
 *
 * `messageId` scrolls the timeline to the message. Exported because the
 * notification entity is not the only thing that points at a message — an OS
 * banner and an in-app toast are raised straight from the socket frame, and a
 * link that omits the param drops the reader at the conversation's default view
 * instead.
 */
export function buildChannelMessageLocation({
  channelId,
  messageId,
}: {
  channelId: string
  messageId?: string | null
}): string {
  const path = resolveRoute(ROUTES.MESSAGES_CHANNEL, { channelId })
  const query = encodeQueryString(messageId ? { messageId } : {})
  return withQuery(path, query)
}

// Same "latest active, else latest archived" precedence as the server-side
// ordering (server/src/services/prospects/helpers.ts:orderByLatestActiveOpsReporting)
// applied client-side to an already-fetched array, since callers here (e.g.
// notification links) aren't always backed by that ordered query.
export function pickLatestActiveOpsReportingId(
  opsReportings: IRankableOpsReporting[] | null | undefined,
): string | null {
  if (!opsReportings?.length) {
    return null
  }
  const latest = opsReportings.reduce((best, candidate) => {
    const bestIsArchived = best.status === OPSReportingStatus.Archived
    const candidateIsArchived = candidate.status === OPSReportingStatus.Archived
    if (bestIsArchived !== candidateIsArchived) {
      return candidateIsArchived ? best : candidate
    }
    const bestCreatedAt = best.createdAt ? new Date(best.createdAt).getTime() : 0
    const candidateCreatedAt = candidate.createdAt ? new Date(candidate.createdAt).getTime() : 0
    return candidateCreatedAt > bestCreatedAt ? candidate : best
  })
  return latest.id
}

/**
 * The commentary box a note belongs to, plus the id of the note that OWNS it.
 *
 * A reply never inherits `commentary_id`, so the box hangs off an ancestor: the
 * immediate parent for a direct reply, and the thread root for anything deeper.
 * The owning note is what `?scrollTo` has to name — it is the only note in the
 * thread the commentary surfaces render an anchor for, so pointing at the middle
 * of a deep thread lands the reader at the top of the page.
 */
function resolveCommentaryAnchor(
  note: INotificationRoutingNote,
): { commentary: INotificationRoutingCommentary; noteId: string } | null {
  if (note.commentary) {
    return { commentary: note.commentary, noteId: note.id }
  }
  if (note.parent?.commentary) {
    return { commentary: note.parent.commentary, noteId: note.parentId || note.id }
  }
  if (note.threadRoot?.commentary) {
    return { commentary: note.threadRoot.commentary, noteId: note.threadRoot.id }
  }
  return null
}

export function buildNoteRoute(note: INotificationRoutingNote): string | null {
  return nullOnMissingRouteParam(() => resolveNoteRoute(note))
}

function resolveNoteRoute(note: INotificationRoutingNote): string | null {
  const noteId = note.parentId || note.id

  // A reply on a Loan Committee Report commentary. This used to test
  // `note.lcrCommentary.sectionEntity`, which the entity stopped having when
  // the LCR tables were generalized (`notes.lcr_commentary_id` became
  // `commentary_id`) — the branch could never fire, so every LCR reply fell
  // through to the client page instead of the report. It now reads the live
  // relation, and the report being one scrolling page means no per-section
  // sub-path is needed: `?scrollTo` finds the box wherever it sits.
  const commentaryAnchor = resolveCommentaryAnchor(note)
  const commentary = commentaryAnchor?.commentary
  // Every commentary surface scrolls to the note that owns the box, which is not
  // always `parentId` — see `resolveCommentaryAnchor`.
  const commentaryNoteId = commentaryAnchor?.noteId ?? noteId
  // Test the section's kind rather than inferring the surface from which owner
  // columns the note happens to carry. Amendment and OPS commentary notes are
  // created with a null client_name today, so `note.clientInfo` alone would
  // discriminate — but that is an invariant living in a different file, and if
  // it ever changed this would start sending amendment replies to a client's
  // Loan Committee Report with no error anywhere.
  if (commentary?.section?.kind === CommentaryKind.LCR && note.clientInfo) {
    const path = resolveRoute(ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAGE, {
      id: note.clientInfo.id,
    })
    return `${path}?scrollTo=${commentaryNoteId}`
  }
  // The other two commentary surfaces. Neither note carries an owner column of
  // its own, so the page comes off the analysis the box belongs to — and, like
  // the LCR, `?scrollTo` lands the reader on the box itself rather than in the
  // notes panel. Both sit above the owner checks below because the section's
  // kind is what identifies the surface.
  const analysis = commentary?.generalAnalysis
  if (commentary?.section?.kind === CommentaryKind.AmendmentAnalysis && analysis?.clientInfo) {
    const path = resolveRoute(ROUTES.AMENDMENT_ANALYSIS, {
      id: analysis.clientInfo.id,
      analysisId: analysis.id,
    })
    return `${path}?scrollTo=${commentaryNoteId}`
  }
  if (commentary?.section?.kind === CommentaryKind.OpsWriteup && analysis?.opsReportingId) {
    // A write-up is keyed by deal, so its own `opsReportingId` names the exact deal
    // the note was written on — and `backfill-deal-summary-ops-reporting-id` already
    // applied the latest-active-then-latest precedence to every legacy row, leaving
    // NULL only where the client has no deal to point at. Checked before the route
    // is built because `resolveRoute` throws on a missing param.
    //
    // The deep-link is the Summary tab, not `/prospects/:id`: that path is a landing
    // dispatcher that immediately redirects to Overview or Application, neither of
    // which renders the write-up's commentary.
    const path = resolveRoute(ROUTES.PROSPECT_SUMMARY_PAGE, { id: analysis.opsReportingId })
    return `${path}?scrollTo=${commentaryNoteId}`
  }
  if (note.borrowingBase) {
    return withQuery(
      resolveRoute(ROUTES.BBC_SUMMARY, { id: note.borrowingBase.id }),
      buildNoteSearch(noteId),
    )
  }
  if (note.opsReporting) {
    return withQuery(
      resolveRoute(ROUTES.PROSPECT_PAGE, { id: note.opsReporting.id }),
      buildNoteSearch(noteId),
    )
  }
  if (note.ongoingReporting) {
    const search = buildNoteSearch(noteId)
    switch (note.ongoingReporting.type) {
      case REPORTING_DOCUMENT_TYPES.financials:
        return withQuery(
          resolveRoute(ROUTES.ONGOING_REPORTING_SUMMARY, { id: note.ongoingReporting.id }),
          search,
        )
      case REPORTING_DOCUMENT_TYPES.bankTransactions:
        return withQuery(
          resolveRoute(ROUTES.ONGOING_REPORTING_BANK_TRANSACTIONS_UPLOAD, {
            id: note.ongoingReporting.id,
          }),
          search,
        )
      case REPORTING_DOCUMENT_TYPES.salesBySKU:
        return withQuery(
          resolveRoute(ROUTES.ONGOING_REPORTING_SALES_BY_SKU, { id: note.ongoingReporting.id }),
          search,
        )
      case REPORTING_DOCUMENT_TYPES.arGeneralLedger:
        return withQuery(
          resolveRoute(ROUTES.ONGOING_REPORTING_AR_GENERAL_LEDGER, {
            id: note.ongoingReporting.id,
          }),
          search,
        )
      case REPORTING_DOCUMENT_TYPES.capTable:
        return withQuery(
          resolveRoute(ROUTES.ONGOING_REPORTING_CAP_TABLE_UPLOAD, {
            id: note.ongoingReporting.id,
          }),
          search,
        )
      default:
        return null
    }
  }
  const flagId = note.flagId || note.parent?.flagId
  if (flagId) {
    return withQuery(ROUTES.HOMEPAGE, encodeQueryString({ tab: 'Flags', flagId, noteId }))
  }
  // Market news replies sit at the bottom of the headline's detail pane, not in the
  // notes column — `replies` tells the page to scroll there rather than just select the row.
  if (note.newsfeedHeadlineId) {
    return withQuery(
      ROUTES.MARKET_NEWS,
      encodeQueryString({ id: note.newsfeedHeadlineId, replies: 'true' }),
    )
  }
  if (note.clientInfo) {
    return withQuery(
      resolveRoute(ROUTES.CLIENT_PAGE, { id: note.clientInfo.id }),
      buildNoteSearch(noteId),
    )
  }
  const entityNoteMention = note.noteMentions?.find(({ entity }) => !!entity)
  if (entityNoteMention?.entityId) {
    return withQuery(
      resolveRoute(ROUTES.ENTITY_PAGE, { id: entityNoteMention.entityId }),
      buildNoteSearch(noteId),
    )
  }
  return null
}

export function getNotificationLocation(
  notification: INotificationRoutingData,
  context: INotificationRoutingContext,
): string | null {
  return nullOnMissingRouteParam(() => resolveNotificationLocation(notification, context))
}

function resolveNotificationLocation(
  notification: INotificationRoutingData,
  context: INotificationRoutingContext,
): string | null {
  // Both land on the deal's Due Diligence page with the notes list expanded on the
  // note in question — a reply on one, and for a logged call one of the questions
  // it answered, which is what puts the reader among the unread answers.
  if (
    notification.type === NotificationType.DefaultDiligenceNoteReply ||
    notification.type === NotificationType.DiligenceCallAnswers
  ) {
    if (!notification.note) {
      return null
    }
    if (notification.note.clientInfo) {
      const noteId = notification.note.parentId || notification.note.id
      return withQuery(
        resolveRoute(ROUTES.DUE_DILIGENCE, { id: notification.note.clientInfo.id }),
        buildNoteSearch(noteId, { dueDiligenceFilters: [NotesFilterOptions.All] }),
      )
    }
    return null
  }

  if (
    [
      NotificationType.NoteTagging,
      NotificationType.NoteReply,
      NotificationType.LCRNoteReply,
    ].includes(notification.type)
  ) {
    if (!notification.note) {
      return null
    }
    return resolveNoteRoute(notification.note)
  }

  if (notification.type === NotificationType.RequestFunding && notification.borrowingBaseId) {
    return resolveRoute(ROUTES.BBC_SUMMARY, { id: notification.borrowingBaseId })
  }

  if (
    notification.type === NotificationType.BBCHistoricalDataFailed &&
    notification.data?.borrowingBaseId
  ) {
    return resolveRoute(ROUTES.BBC_SUMMARY, {
      id: notification.data.borrowingBaseId as string,
    })
  }

  if (notification.type === NotificationType.SystemError) {
    const errorTriageId = notification.data?.errorTriageId
    return typeof errorTriageId === 'string' && errorTriageId
      ? withQuery(ROUTES.ERROR_CENTER, encodeQueryString({ id: errorTriageId }))
      : ROUTES.ERROR_CENTER
  }

  if (
    notification.type === NotificationType.BBCForeignInventoryLocationZeroBalance &&
    notification.borrowingBaseId
  ) {
    return resolveRoute(ROUTES.BBC_COLLATERAL_ELIGIBILITY_INVENTORY, {
      id: notification.borrowingBaseId,
    })
  }

  if (notification.type === NotificationType.BBCSigned) {
    return `${ROUTES.OUTGOING_WIRE}?tab=Fundings`
  }

  if (
    [NotificationType.SubmitReporting, NotificationType.CodatPortfolioSync].includes(
      notification.type,
    )
  ) {
    return `${ROUTES.ACTIVITY_QUEUE}?tab=Reporting`
  }

  if (notification.type === NotificationType.OPSSync && notification.data?.opsReportingId) {
    return resolveRoute(ROUTES.PROSPECT_ANALYSIS_QUEUE, {
      id: notification.data.opsReportingId as string,
    })
  }

  if (
    notification.type === NotificationType.OPSProcessingComplete &&
    notification.data?.opsReportingId
  ) {
    return resolveRoute(ROUTES.PROSPECT_ANALYSIS_QUEUE, {
      id: notification.data.opsReportingId as string,
    })
  }

  if (
    notification.type === NotificationType.OpsWriteupInReview &&
    notification.data?.opsReportingId
  ) {
    return resolveRoute(ROUTES.PROSPECT_SUMMARY_PAGE, {
      id: notification.data.opsReportingId as string,
    })
  }

  if (
    [NotificationType.SubmitOPS, NotificationType.ApproveOPS].includes(notification.type) &&
    notification.data?.opsReportingId
  ) {
    return resolveRoute(ROUTES.PROSPECT_MANAGE_TERMS_PAGE, {
      id: notification.data.opsReportingId as string,
    })
  }

  if (
    [
      NotificationType.ActivityAssign,
      NotificationType.ChecksPostingIssue,
      NotificationType.BBCProcessingSkipped,
      NotificationType.CheckUploadReminder,
    ].includes(notification.type)
  ) {
    return ROUTES.ACTIVITY_QUEUE
  }

  if (notification.type === NotificationType.ClientIntakeSubmit) {
    if (notification.data?.opsReportingId) {
      return resolveRoute(ROUTES.PROSPECT_APPLICATION_PAGE, {
        id: notification.data.opsReportingId as string,
      })
    }
    const latestOpsReportingId = pickLatestActiveOpsReportingId(
      notification.clientInfo?.opsReportings,
    )
    if (latestOpsReportingId) {
      return resolveRoute(ROUTES.PROSPECT_APPLICATION_PAGE, { id: latestOpsReportingId })
    }
  }

  // A reactivation is a "pick this lead back up" prompt, so it lands on the
  // prospect's overview — the same place the notification email links to. The
  // reopened reporting is the newly rotated version, hence the latest-active
  // fallback when the sender didn't carry an id.
  if (notification.type === NotificationType.ProspectReactivated) {
    if (notification.data?.opsReportingId) {
      return resolveRoute(ROUTES.PROSPECT_OVERVIEW_PAGE, {
        id: notification.data.opsReportingId as string,
      })
    }
    const latestOpsReportingId = pickLatestActiveOpsReportingId(
      notification.clientInfo?.opsReportings,
    )
    if (latestOpsReportingId) {
      return resolveRoute(ROUTES.PROSPECT_OVERVIEW_PAGE, { id: latestOpsReportingId })
    }
  }

  if (
    [NotificationType.AutoReportingConnected, NotificationType.AutoReportingDisconnected].includes(
      notification.type,
    ) &&
    notification.data?.clientStatus === ClientInfoStatus.Prospect &&
    notification.data?.opsReportingId
  ) {
    return resolveRoute(ROUTES.PROSPECT_PAGE, {
      id: notification.data.opsReportingId as string,
    })
  }

  if (notification.type === NotificationType.PlaidConnected && notification.clientInfo) {
    if (notification.data?.clientStatus === ClientInfoStatus.DueDiligence) {
      return resolveRoute(ROUTES.DUE_DILIGENCE, {
        id: notification.clientInfo.id,
      })
    }
    return resolveRoute(ROUTES.CLIENT_PAGE, { id: notification.clientInfo.id })
  }

  if (
    notification.type === NotificationType.ClientApplicationFileUpload &&
    notification.clientInfo
  ) {
    return `${resolveRoute(ROUTES.DUE_DILIGENCE_VALIDATE_DOCUMENTS_PAGE, {
      id: notification.clientInfo.id,
    })}?documentRequestId=${notification.ddDocumentRequestId}`
  }

  if (notification.type === NotificationType.CodatUWSync && notification.clientInfo) {
    return resolveRoute(ROUTES.DUE_DILIGENCE_ANALYSIS_QUEUE, {
      id: notification.clientInfo.id,
    })
  }

  if (
    [
      NotificationType.DDDocumentStatusReminder,
      NotificationType.ClientApplicationStepFinished,
    ].includes(notification.type) &&
    notification.clientInfo
  ) {
    // The standalone team page was retired — the live team now lives on the DD
    // Overview (and the submitted values in its application modal), so the Team
    // step falls through to the same Overview route as the default case below.
    if (notification.data?.step === DueDiligenceDocumentRequestStep.Financials) {
      return resolveRoute(ROUTES.DUE_DILIGENCE_CLIENT_FINANCIALS_PAGE, {
        id: notification.clientInfo.id,
      })
    }
    return resolveRoute(ROUTES.DUE_DILIGENCE, {
      id: notification.clientInfo.id,
    })
  }

  if (notification.type === NotificationType.ClientApplicationDocumentComment) {
    if (context.isClientUser) {
      const docTypeId = notification.ddDocumentRequest?.type?.id
      return docTypeId ? `${ROUTES.DOCUMENTS_PAGE}?docTypeId=${docTypeId}` : ROUTES.DOCUMENTS_PAGE
    } else {
      return `${resolveRoute(ROUTES.DUE_DILIGENCE_VALIDATE_DOCUMENTS_PAGE, {
        id: notification.clientInfo?.id || '',
      })}?documentRequestId=${notification.ddDocumentRequestId}&isComment=true`
    }
  }

  if (
    [
      NotificationType.OveradvanceCreated,
      NotificationType.OveradvanceUpdated,
      NotificationType.OveradvanceUpdatedPriority,
      NotificationType.OveradvanceDeleted,
    ].includes(notification.type) &&
    notification.clientInfo
  ) {
    return resolveRoute(ROUTES.CLIENT_SETUP_LOAN_STRUCTURE_OVERADVANCE, {
      id: notification.clientInfo.id,
    })
  }

  if (notification.type === NotificationType.NoticePeriodReminder && notification.clientInfo) {
    return resolveRoute(ROUTES.CLIENT_SETUP_LOAN_STRUCTURE_ASSET_BASED, {
      id: notification.clientInfo.id,
    })
  }

  if (
    [
      NotificationType.AmendmentCreated,
      NotificationType.AmendmentUpdated,
      NotificationType.AmendmentDeleted,
    ].includes(notification.type) &&
    notification.clientInfo
  ) {
    return atlasUrls.amendment(
      notification.clientInfo.id,
      notification.data?.amendmentId ? String(notification.data.amendmentId) : null,
    )
  }

  if (
    [
      NotificationType.CheckDataCreated,
      NotificationType.CheckDataUpdated,
      NotificationType.CheckDataDeleted,
    ].includes(notification.type) &&
    notification.clientInfo
  ) {
    const checkParam = notification.checkData?.id ? `&checkId=${notification.checkData.id}` : ''
    return `${resolveRoute(ROUTES.CLIENT_PAGE, {
      id: notification.clientInfo.id,
    })}?tab=Activity&scrollTo=accountActivity${checkParam}`
  }

  if (
    [
      NotificationType.TermLoanCreated,
      NotificationType.TermLoanUpdated,
      NotificationType.TermLoanDeleted,
      NotificationType.TermLoanActivityCreated,
      NotificationType.TermLoanActivityUpdated,
      NotificationType.TermLoanActivityDeleted,
    ].includes(notification.type) &&
    notification.clientInfo
  ) {
    return `${resolveRoute(ROUTES.CLIENT_PAGE, {
      id: notification.clientInfo.id,
    })}?tab=Activity&scrollTo=term-loans`
  }

  if (
    (notification.type === NotificationType.TaskMention ||
      notification.type === NotificationType.TaskCollaboratorActivity ||
      notification.type === NotificationType.TaskAssigned) &&
    notification.data?.taskId
  ) {
    return atlasUrls.task(String(notification.data.taskId))
  }

  if (
    [
      NotificationType.ChannelMention,
      NotificationType.ChannelMessage,
      // The conversation it was meant for, where the failed message waits above the composer.
      NotificationType.ScheduledChannelMessageFailed,
    ].includes(notification.type) &&
    notification.data?.channelId
  ) {
    return buildChannelMessageLocation({
      channelId: String(notification.data.channelId),
      messageId: notification.data.messageId ? String(notification.data.messageId) : null,
    })
  }

  if (notification.clientInfo && context.isUW) {
    return resolveRoute(ROUTES.DUE_DILIGENCE, {
      id: notification.clientInfo.id,
    })
  }

  if (notification.clientInfo && context.isBDO) {
    const latestOpsReportingId = pickLatestActiveOpsReportingId(
      notification.clientInfo.opsReportings,
    )
    if (latestOpsReportingId) {
      return resolveRoute(ROUTES.PROSPECT_PAGE, { id: latestOpsReportingId })
    }
  }

  if (notification.clientInfo) {
    return resolveRoute(ROUTES.CLIENT_PAGE, { id: notification.clientInfo.id })
  }

  if (notification.type === NotificationType.MarketNews) {
    return ROUTES.MARKET_NEWS
  }

  /**
   * A run is read in its conversation now, not in the retired Arc chat page.
   *
   * `channelId` is written by ExecuteArcAutomations for every run that has a
   * conversation — which is every run since `startScheduledArcRun` began creating
   * one. Older notifications carry only `sessionId`, and there is nothing left that
   * can open a bare session: `/atlas-ai/chat` redirects to Messages and drops the
   * query string, so the link used to look live and go nowhere. Those land on
   * Messages itself instead of on a dead deep link.
   */
  if (
    [NotificationType.ArcAutomationComplete, NotificationType.ArcWatchFired].includes(
      notification.type,
    )
  ) {
    if (notification.data?.channelId) {
      return `${ROUTES.MESSAGES}/${encodeURIComponent(String(notification.data.channelId))}`
    }
    if (notification.data?.sessionId) return ROUTES.MESSAGES
  }

  // A shared conversation opens in Messages, where the landing modal previews it
  // and offers to join. The token in `shareLinkId` may be either kind — a channel
  // link or a legacy Arc session one — and the resolver accepts both.
  if (notification.type === NotificationType.AtlasChatShared && notification.data?.shareLinkId) {
    return `${ROUTES.MESSAGES}?share=${encodeURIComponent(String(notification.data.shareLinkId))}`
  }

  /**
   * Email triage has no single Atlas destination worth deep-linking to: the four
   * buckets produce a Gmail draft, an ongoing-reporting record, a BBC, an ops
   * reporting and a Salesforce lead, and `resultId` is a breadcrumb that nothing
   * reads programmatically (intake doesn't even surface one). The actionable link
   * is the email itself — an external URL this resolver can't return, since
   * callers navigate the result internally, so it rides in the DM body instead.
   */
  if (
    [
      NotificationType.ArcReminder,
      NotificationType.ArcEmailDraftPrepared,
      NotificationType.ArcEmailReportingFiled,
      NotificationType.ArcEmailIntakeFiled,
      NotificationType.ArcEmailLeadReferral,
    ].includes(notification.type)
  ) {
    return ROUTES.NOTIFICATIONS
  }

  // The call has no client page to open — it matched none — so the row opens the
  // recording the developments came from (an external link opens in a new tab).
  if (notification.type === NotificationType.UnmatchedCallMajorDevelopments) {
    const transcriptUrl = notification.data?.transcriptUrl
    return typeof transcriptUrl === 'string' && /^https?:\/\//.test(transcriptUrl)
      ? transcriptUrl
      : null
  }

  // Balance-reconciliation alerts. These are raised by a cron
  // (checkAccountActivityLoanBalanceMatch) with `entity` null, so the row carries
  // only `clientName` in its data — there is no client id to build a client-page
  // link from. Without a branch here they fell through to `null`, and a
  // notification with no destination renders as a plain, unclickable row: on the
  // dashboard these alerts are most of the "Waiting On Me" widget, so the whole
  // list read as broken. Send them to the notifications screen, where the full
  // alert text and its filters are.
  if (
    [
      NotificationType.AccountActivityTotalBalanceMismatch,
      NotificationType.AccountActivityAblBalanceMismatch,
      NotificationType.AccountActivityTotalBalanceMismatchResolved,
      NotificationType.AccountActivityAblBalanceMismatchResolved,
    ].includes(notification.type)
  ) {
    return ROUTES.NOTIFICATIONS
  }

  if ([NotificationType.UserCreated, NotificationType.UserDeleted].includes(notification.type)) {
    if (notification.data?.clientInfoId) {
      return resolveRoute(ROUTES.CLIENT_SETUP_USERS, {
        id: notification.data.clientInfoId as string,
      })
    }
    return ROUTES.USER_MANAGEMENT
  }

  return null
}
